import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { X, Paperclip, Send, HardDrive } from "lucide-react";
import { createPortal } from "react-dom";
import { api, getUser } from "../api";
import { toast } from "sonner";
import { Linkify } from "./Linkify";
import { PdfViewer } from "./PdfViewer";
import { ZoomableImage } from "./ZoomableImage";
import { DaySeparator, MessageTicks, messageStatus, localDayKey, dayLabel, timeHHmm } from "./MessageMeta";

// Pannello chat DENTRO la sala video: riusa integralmente gli endpoint /chat/* (stessa conversazione,
// stessi messaggi, stesso letto/non letto, stessi allegati). Nessuna seconda chat, niente WebRTC/WS/data channel.
// Realtime = polling 5s come la chat ordinaria. Aperto -> /chat/messages (marca letti). Chiuso -> /chat/conversations (solo conteggio).
export function VideoChatPanel({ peerId, open, onClose, onUnread, embedded = false }) {
  const { t, i18n } = useTranslation();
  const u = getUser();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [canSend, setCanSend] = useState(true);
  const [offline, setOffline] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [drivePicker, setDrivePicker] = useState(false);
  const [driveFiles, setDriveFiles] = useState(null);
  const [savedSet, setSavedSet] = useState({});
  const isUser = u?.role === "user";
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const liveRef = useRef(null);
  const lastInboundId = useRef(null);
  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);

  const announce = (msg) => { if (liveRef.current) liveRef.current.textContent = msg; };

  const loadMessages = useCallback(async () => {
    if (!peerId) return;
    try {
      const { data } = await api.get(`/chat/messages/${peerId}`);
      setMsgs(data.messages); setCanSend(data.can_send); setOffline(false);
      onUnread?.(0);
      window.dispatchEvent(new Event("chat-updated"));
      const last = data.messages[data.messages.length - 1];
      if (last && last.from_id !== u?.id && last.id !== lastInboundId.current) {
        lastInboundId.current = last.id; announce(t("video.chat_new"));
      }
    } catch (e) {
      const st = e?.response?.status;
      if (st === 403) setCanSend(false);
      else setOffline(true);
    }
  }, [peerId, onUnread, u?.id, t]);

  const loadUnread = useCallback(async () => {
    if (!peerId) return;
    try {
      const { data } = await api.get("/chat/conversations");
      const c = data.find(x => x.other?.id === peerId);
      onUnread?.(c?.unread || 0); setOffline(false);
    } catch { setOffline(true); }
  }, [peerId, onUnread]);

  // Poll unico: usa lo stato corrente (openRef) per non marcare letto quando chiuso.
  useEffect(() => {
    let stop = false;
    const tick = () => { if (!stop) (openRef.current ? loadMessages() : loadUnread()); };
    tick();
    const iv = setInterval(tick, 5000);
    return () => { stop = true; clearInterval(iv); };
  }, [open, loadMessages, loadUnread]);

  useEffect(() => { if (open) { loadMessages(); const id = setTimeout(() => inputRef.current?.focus(), 60); return () => clearTimeout(id); } }, [open]); // eslint-disable-line
  useEffect(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; }, [msgs, open]);
  useEffect(() => () => { if (viewer?.url) URL.revokeObjectURL(viewer.url); }, [viewer]);

  const send = async () => {
    if (!text.trim() || !peerId) return;
    const body = text; setText("");
    try { await api.post("/chat/send", { to_user_id: peerId, body }); await loadMessages(); }
    catch (e) { setText(body); toast.error(e.response?.data?.detail || t("events.error_generic")); }
  };
  const upload = async (e) => {
    const file = e.target.files[0]; if (!file || !peerId) return;
    const fd = new FormData(); fd.append("to_user_id", peerId); fd.append("file", file);
    setUploading(true);
    try { await api.post("/chat/attachments/upload", fd); await loadMessages(); }
    catch (err) {
      const d = err.response?.data?.detail;
      const map = { file_too_large: "video.chat_file_too_large", extension_not_allowed: "video.chat_file_type", mime_not_allowed: "video.chat_file_type", file_signature_mismatch: "video.chat_file_type" };
      const msg = map[d] ? t(map[d]) : (typeof d === "string" ? d : t("events.error_generic"));
      toast.error(msg); announce(msg);
    } finally { setUploading(false); e.target.value = ""; }
  };
  const openDrive = async () => {
    setDrivePicker(true); setDriveFiles(null);
    try { const { data } = await api.get("/drive"); setDriveFiles(data); }
    catch { setDriveFiles([]); toast.error(t("video.drive_error")); }
  };
  const sendFromDrive = async (fid) => {
    const fd = new FormData(); fd.append("to_user_id", peerId);
    try { await api.post(`/drive/${fid}/send-to-chat`, fd); setDrivePicker(false); await loadMessages(); }
    catch (e) { toast.error(e.response?.data?.detail || t("video.drive_error")); }
  };
  const saveToDrive = async (aid) => {
    if (savedSet[aid] === "saving" || savedSet[aid] === "ok") return;   // idempotente lato UI
    setSavedSet(s => ({ ...s, [aid]: "saving" }));
    try { await api.post(`/chat/attachments/${aid}/save-to-drive`); setSavedSet(s => ({ ...s, [aid]: "ok" })); }
    catch (e) { setSavedSet(s => ({ ...s, [aid]: "err" })); toast.error(e.response?.data?.detail || t("video.drive_error")); }
  };
  const fetchBlob = async (url) => {
    const { data, headers } = await api.get(url, { responseType: "blob" });
    const ct = (headers["content-type"] || data.type || "application/octet-stream").split(";")[0];
    return new Blob([data], { type: ct });
  };
  const dl = (blob, filename) => { const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 60000); };
  const openAtt = async (aid, filename) => {
    try {
      const blob = await fetchBlob(`/chat/attachments/${aid}/view`);
      if (blob.type === "application/pdf") setViewer({ blob, filename, contentType: blob.type });
      else if (blob.type.startsWith("image/") || blob.type.startsWith("video/")) setViewer({ url: URL.createObjectURL(blob), filename, contentType: blob.type });
      else dl(blob, filename);
    } catch (e) { const st = e?.response?.status; toast.error(st === 403 ? t("pdfx.err_forbidden") : st === 404 ? t("pdfx.err_missing") : t("drive.open_error")); }
  };
  const downloadAtt = async (aid, filename) => { try { dl(await fetchBlob(`/chat/attachments/${aid}/view`), filename); } catch { toast.error(t("drive.open_error")); } };
  const closeViewer = () => { if (viewer?.url) URL.revokeObjectURL(viewer.url); setViewer(null); };
  const onKey = (e) => { if (e.key === "Escape") onClose?.(); };

  if (!open) return null;

  return (
    <div role="dialog" aria-label={t("video.chat_title")} onKeyDown={onKey}
      data-testid="video-chat-panel"
      className={embedded
        ? "flex flex-col bg-white border border-slate-200 rounded-lg h-full w-full overflow-hidden"
        : "flex flex-col bg-white border border-slate-200 shadow-2xl fixed inset-x-0 bottom-0 z-40 max-h-[52vh] rounded-t-2xl lg:static lg:inset-auto lg:z-auto lg:max-h-none lg:h-full lg:w-[340px] lg:shrink-0 lg:rounded-lg lg:shadow-none"}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 shrink-0">
        <span className="font-semibold text-sm">{t("video.chat_title")}</span>
        <button type="button" onClick={onClose} aria-label={t("video.chat_close")} title={t("video.chat_close")}
          data-testid="video-chat-close" className="p-2 -mr-1 rounded hover:bg-slate-100 min-h-[40px] min-w-[40px] flex items-center justify-center"><X size={18} /></button>
      </div>
      {offline && <div className="px-3 py-1.5 text-xs text-amber-700 bg-amber-50 border-b border-amber-100 shrink-0" role="status" data-testid="video-chat-offline">{t("video.chat_offline")}</div>}
      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2" data-testid="video-chat-messages">
        {msgs.length === 0 && <div className="text-xs text-slate-400 text-center mt-4">{t("chat.empty_conv")}</div>}
        {msgs.map((m, i) => {
          const prev = msgs[i-1];
          const showSep = !prev || localDayKey(prev.at) !== localDayKey(m.at);
          const mine = m.from_id === u?.id;
          const status = messageStatus(m, u?.id);
          return (<div key={m.id}>
            {showSep && <DaySeparator label={dayLabel(m.at, t, i18n.language)} />}
            <div className={`max-w-[85%] p-2 rounded-xl ${mine ? "ml-auto bg-blue-500 text-white" : "bg-slate-100"}`}>
              {m.type === "attachment" ? (
                <div>
                  <div className="text-xs opacity-70">📎 {m.body} {m.read ? `· ${t("chat.read")}` : `· ${t("chat.unread")}`}</div>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    <button className="text-xs underline" onClick={() => openAtt(m.attachment_id, m.body)} data-testid="video-chat-att-view">{u?.role === "professional" ? t("chat.view_only") : t("chat.open")}</button>
                    {["user","professional","organization"].includes(u?.role) && <button className="text-xs underline" onClick={() => downloadAtt(m.attachment_id, m.body)} data-testid="video-chat-att-download">⬇ {t("chat.download")}</button>}
                  </div>
                  {!mine && isUser && (
                    <button className="text-xs underline mt-1 disabled:opacity-60" onClick={() => saveToDrive(m.attachment_id)} disabled={savedSet[m.attachment_id] === "ok" || savedSet[m.attachment_id] === "saving"} data-testid="video-att-save-drive">
                      {savedSet[m.attachment_id] === "ok" ? `✓ ${t("video.saved_to_drive")}` : t("video.save_to_drive")}
                    </button>)}
                </div>
              ) : <div className="text-sm whitespace-pre-wrap break-words"><Linkify text={m.body} linkClassName={`underline break-all ${mine ? "text-white" : "text-blue-600"}`} /></div>}
              <div className={`flex items-center gap-1 justify-end mt-0.5 text-[10px] ${mine ? "text-white/70" : "text-slate-400"}`}>
                <span data-testid="msg-time">{timeHHmm(m.at, i18n.language)}</span>
                {mine && <MessageTicks status={status} t={t} />}
              </div>
            </div>
          </div>);
        })}
      </div>
      {canSend ? (
        <div className="flex gap-2 p-2 border-t border-slate-100 shrink-0">
          <input ref={inputRef} className="yp-input" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder={t("chat.input_ph")} aria-label={t("chat.input_ph")} data-testid="video-chat-input" />
          <button type="button" className="yp-btn yp-btn-ghost !px-2.5 shrink-0 disabled:opacity-50" onClick={() => fileRef.current?.click()} disabled={uploading} aria-label={t("video.attach_device")} title={t("video.attach_device")} data-testid="video-chat-attach">{uploading ? <span className="inline-block w-[18px] h-[18px] border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Paperclip size={18} />}</button>
          {isUser && <button type="button" className="yp-btn yp-btn-ghost !px-2.5 shrink-0" onClick={openDrive} aria-label={t("video.from_drive")} title={t("video.from_drive")} data-testid="video-drive-btn"><HardDrive size={18} /></button>}
          <button type="button" className="yp-btn yp-btn-primary !px-2.5 shrink-0" onClick={send} aria-label={t("chat.send")} title={t("chat.send")} data-testid="video-chat-send"><Send size={18} /></button>
          <input ref={fileRef} type="file" className="hidden" accept=".pdf,image/*,video/mp4,video/webm" onChange={upload} data-testid="video-chat-file-input" />
        </div>
      ) : <div className="text-sm text-slate-500 text-center p-3 bg-slate-50 shrink-0" data-testid="video-chat-readonly">{t("chat.read_only")}</div>}
      <span ref={liveRef} className="sr-only" aria-live="polite" role="status" />
      {drivePicker && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-end lg:items-center justify-center" data-testid="video-drive-picker" onClick={() => setDrivePicker(false)}>
          <div className="bg-white w-full lg:max-w-md rounded-t-2xl lg:rounded-2xl max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <span className="font-semibold text-sm">{t("video.from_drive")}</span>
              <button onClick={() => setDrivePicker(false)} aria-label={t("video.chat_close")} data-testid="video-drive-close" className="p-2 min-h-[40px] min-w-[40px] flex items-center justify-center"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {driveFiles === null ? <div className="text-slate-400 text-sm">{t("common.loading")}</div>
                : driveFiles.length === 0 ? <div className="text-slate-400 text-sm text-center py-6" data-testid="video-drive-empty">{t("video.drive_empty")}</div>
                  : <ul className="space-y-1">{driveFiles.map(f => (
                    <li key={f.id}><button className="w-full text-left px-3 py-2 rounded hover:bg-slate-50 text-sm flex items-center gap-2 min-h-[44px]" onClick={() => sendFromDrive(f.id)} data-testid="video-drive-file"><Paperclip size={14} aria-hidden="true" /><span className="truncate">{f.filename}</span></button></li>))}</ul>}
            </div>
          </div>
        </div>, document.body)}
      {viewer?.contentType === "application/pdf" && <PdfViewer blob={viewer.blob} filename={viewer.filename} onClose={closeViewer} testid="video-chat-pdf-viewer" />}
      {viewer && viewer.contentType !== "application/pdf" && createPortal((
        <div className="fixed inset-0 z-[9998] bg-black/70 flex flex-col" onClick={closeViewer} data-testid="video-chat-att-viewer">
          <div className="flex items-center p-3 text-white" onClick={e => e.stopPropagation()}>
            <span className="text-sm truncate">{viewer.filename}</span>
          </div>
          <div className="flex-1 flex items-center justify-center p-4 overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="relative inline-block max-w-full max-h-full">
              <button type="button" onClick={closeViewer} data-testid="video-chat-att-viewer-close" aria-label={t("video.chat_close")}
                className="absolute top-1 right-1 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-black/70 text-white text-xl leading-none shadow-lg active:scale-95">✕</button>
              {viewer.contentType?.startsWith("video/")
                ? <video src={viewer.url} controls autoPlay className="block max-w-full max-h-full rounded" data-testid="video-chat-att-viewer-video" />
                : <ZoomableImage src={viewer.url} alt={viewer.filename} testid="video-chat-att-viewer-image" />}
            </div>
          </div>
        </div>), document.body)}
    </div>
  );
}
